'use client';

import { FC, useState } from 'react';

import { useAuthentication, UserGroups } from '@ravago/shared/authentication';
import { UserPermissions } from '@ravago/shared/page-data/models/config';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { useSearchParams } from 'next/navigation';

import { usePathLocale } from '../../../../hooks/use-path-locale';
import { ColorPickerValue } from '../../../shared/extended-color-picker/extended-color-picker';
import { Link } from '../../../shared/link/link';
import { LoginButton } from '../../../shared/login-button/login-button';

interface Props {
   userPermissions: UserPermissions;
   isTailCatalog: boolean;
   color: ColorPickerValue | undefined;
   quantityAmount: number | undefined;
   openFlyout: () => void;
   newQuote: () => void;
   continueCallOff: () => void;
}

export const RequestForQuotationButton: FC<Props> = ({
   userPermissions,
   isTailCatalog,
   color,
   quantityAmount,
   openFlyout,
   newQuote,
   continueCallOff
}) => {
   const translate = useTranslations();
   const { user } = useAuthentication();
   const locale = usePathLocale();
   const searchParams = useSearchParams();

   const [requestButtonLabel, setRequestButtonLabel] = useState(
      searchParams.get('flyout') === 'open' ? 'request-for-quotation.continue-quote-request' : 'request-for-quotation.start-quote-request'
   );

   const onStartQuoteClick = () => {
      openFlyout();
      setRequestButtonLabel('request-for-quotation.continue-quote-request');
   };

   const continueCallOffOrderClick = () => {
      continueCallOff();
   };

   const newQuoteFromCallOffClick = () => {
      newQuote();
   };

   const newQuoteFromQuotedClick = () => {
      newQuote();
   };

   if (isTailCatalog) {
      return null;
   }

   if (!user && !userPermissions.hasAnonymousPermission) {
      return (
         <LoginButton
            data-testid="rfq-button__login"
            trailingIcon="long-arrow-right"
            fullwidth
            buttonLabel={translate('request-for-quotation.login-to-request')}
         />
      );
   }

   if (user?.groups?.includes(UserGroups.digitalPlatformPendingVerification) && !userPermissions.hasPendingPermission) {
      return (
         <Button data-testid="rfq-button__pending" fullWidth trailingIcon="clock" variant="secondary" disabled>
            {translate('request-for-quotation.waiting-verification')}
         </Button>
      );
   }

   if (user?.groups?.includes(UserGroups.digitalPlatformGuest) && !userPermissions.hasGuestPermission) {
      return (
         <Link underline="none" href={`/${locale}/contact`}>
            <Button data-testid="rfq-button__guest" fullWidth trailingIcon="long-arrow-right" variant="secondary">
               {translate('request-for-quotation.contact-us')}
            </Button>
         </Link>
      );
   }

   if (color && !!color.callOffOrderId) {
      return (
         <Box direction="column" gap="md">
            <Button
               data-testid="rfq-button__continue"
               fullWidth
               trailingIcon="long-arrow-right"
               disabled={!quantityAmount}
               onClick={continueCallOffOrderClick}
            >
               {translate('request-for-quotation.continue')}
            </Button>
            <Typography align="center">{translate('request-for-quotation.or')}</Typography>
            <Button data-testid="rfq-button__start" fullWidth trailingIcon="plus" variant="secondary" onClick={newQuoteFromCallOffClick}>
               {translate('request-for-quotation.new-quote')}
            </Button>
         </Box>
      );
   }

   if (color && color.isQuotedColor) {
      return (
         <Box direction="column" gap="md">
            <Typography align="center">{translate('request-for-quotation.or')}</Typography>
            <Button data-testid="rfq-button__start" fullWidth trailingIcon="plus" variant="secondary" onClick={newQuoteFromQuotedClick}>
               {translate('request-for-quotation.new-quote')}
            </Button>
         </Box>
      );
   }

   return (
      <Button
         data-testid="rfq-button__start"
         fullWidth
         trailingIcon="long-arrow-right"
         disabled={!color}
         variant="primary"
         onClick={onStartQuoteClick}
         id="start-request-quote-btn"
      >
         {translate(requestButtonLabel)}
      </Button>
   );
};
